import * as React from "react";
import { useContext } from "react";
import { Button } from "../../../components/ui/button";
import { CardTitle } from "../../../components/ui/card";
import { Stack } from "../../../components/ui/stack";
import { Badge } from "../../../components/ui/badge";
import { TypographyTable } from "../../../components/ui/typography";
import { Skeleton } from "../../../components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import { useWorkspaceContextSlug } from "../../../utils/hooks";
import { createSearchParams, Link as RouterLink } from "react-router-dom";
import { Urls } from "../../../domain/urls";
import { UserContext } from "../../../crud/user/context";

import { ClusterFrontendSchema } from "../../../api-client";
import { backendToCloudProvider } from "../../Setup/Forms/DockerSettingsForm";
import { formatISO } from "date-fns";
import { Light as SyntaxHighlighter } from "react-syntax-highlighter";
import syntaxTheme from "../../../shared-components/syntaxTheme";

interface ClusterInformation {
  cluster?: ClusterFrontendSchema;
  loading?: boolean;
}

export const ClusterInformationDetails = ({
  cluster,
  loading,
}: ClusterInformation): React.ReactElement => {
  const [openDaskConfig, setOpenDaskConfig] = React.useState(false);
  const { user } = useContext(UserContext);
  const account = useWorkspaceContextSlug();

  const desiredZone: string =
    cluster?.clusterOptions?.zoneName ||
    cluster?.clusterOptions?.regionName ||
    "";
  const schedulerZone = cluster?.scheduler.instance?.zoneName
    ? cluster?.scheduler.instance.zoneName
    : "";
  const workerZoneCounts = cluster?.workersExceptUnprovisionable.reduce(
    (accumulator, currentValue) => {
      if (currentValue.instance?.zoneName) {
        accumulator[currentValue.instance?.zoneName] =
          (accumulator[currentValue.instance?.zoneName] || 0) + 1;
      }
      return accumulator;
    },
    Object() as Record<string, number>,
  );
  const workerZones = workerZoneCounts ? Object.keys(workerZoneCounts) : [];
  let clusterZone = <></>;
  if (workerZoneCounts && workerZones) {
    clusterZone = (
      <>
        {schedulerZone}
        {(workerZones.length > 1 || workerZones[0] !== schedulerZone) &&
          " (scheduler)"}
        {(workerZones.length > 1 || workerZones[0] !== schedulerZone) &&
          Object.entries(workerZoneCounts).map(([zone, count]) => (
            <>
              <br />
              {zone} ({count} workers)
            </>
          ))}
      </>
    );
  } else if (schedulerZone) {
    clusterZone = <>{schedulerZone}</>;
  } else if (desiredZone) {
    clusterZone = <>{desiredZone} (requested)</>;
  }

  const firewallDesc = cluster?.clusterOptions?.firewallSpec
    ? cluster?.clusterOptions?.firewallSpec.ingress
        .map((fwRule) => `${fwRule.ports.join()} (${fwRule.cidr})`)
        .join("\n")
    : "";

  const senvLink = cluster?.senvBuildId
    ? `/${Urls.Software}/alias/${cluster.senvAlias?.id}/build/${cluster.senvBuildId}?account=${account}`
    : `/${Urls.Software}/alias/${cluster?.senvAlias?.id}?account=${account}`;
  const grafanaLink = user.isStaff
    ? cluster?.internalGrafanaLink
    : cluster?.externalGrafanaLink;
  const daskConfigYaml = cluster?.daskConfigYaml;
  const clusterTags = user.isStaff
    ? cluster?.tagList
    : cluster?.tagList.filter((value) => !value.systemTag);
  return (
    <Stack direction="row" spacing={6} divider className="flex-wrap">
      <div className="flex-1 min-w-0">
        <CardTitle>Cluster information</CardTitle>
        <TypographyTable
          data={[
            {
              Property: "Name",
              Value: loading ? (
                <Skeleton className="h-4 w-20" />
              ) : (
                cluster?.name
              ),
            },
            {
              Property: "Created",
              Value: loading ? (
                <Skeleton className="h-4 w-32" />
              ) : (
                cluster && formatISO(new Date(cluster.created))
              ),
            },
            {
              Property: "Software",
              Value: loading ? (
                <Skeleton className="h-4 w-24" />
              ) : (
                cluster && (
                  <RouterLink
                    to={senvLink}
                    className="text-inherit hover:underline"
                  >
                    {cluster.senvAlias?.name}
                  </RouterLink>
                )
              ),
            },
            ...(grafanaLink
              ? [
                  {
                    Property: "Monitoring",
                    Value: loading ? (
                      <Skeleton className="h-4 w-16" />
                    ) : (
                      cluster && (
                        <a
                          href={grafanaLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-inherit hover:underline"
                        >
                          Grafana
                        </a>
                      )
                    ),
                  },
                ]
              : []),
          ]}
          showHeaders={false}
          zebraStriping={false}
        />
        {user.isStaff && (
          <Button
            variant="outline"
            onClick={() => setOpenDaskConfig(true)}
            className="mt-4"
          >
            Dask Config
          </Button>
        )}
        <Dialog open={openDaskConfig} onOpenChange={setOpenDaskConfig}>
          <DialogContent className="max-w-8xl max-h-[80vh] overflow-auto">
            <DialogHeader>
              <DialogTitle>Dask Configuration</DialogTitle>
            </DialogHeader>
            <div className="overflow-auto">
              <SyntaxHighlighter language="yaml" style={syntaxTheme}>
                {daskConfigYaml}
              </SyntaxHighlighter>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex-1 min-w-0">
        <CardTitle>Tags</CardTitle>
        {loading && <Skeleton className="h-4 w-20" />}
        {clusterTags && (
          <div className="flex flex-wrap gap-2 max-h-80 overflow-y-auto border rounded-md p-2">
            {clusterTags?.map((tag) => (
              <Badge
                key={tag.label}
                variant={tag.systemTag ? "secondary" : "default"}
                asChild
              >
                <RouterLink
                  to={{
                    pathname: "/clusters",
                    search: createSearchParams({
                      tagFilters: `${tag.label}:${tag.value}`,
                    }).toString(),
                  }}
                >
                  {tag.label}: <strong>{tag.value}</strong>
                </RouterLink>
              </Badge>
            ))}
          </div>
        )}
      </div>

      <div className="flex-1 min-w-0">
        <CardTitle>Infra State</CardTitle>
        <TypographyTable
          data={[
            {
              Property: "State",
              Value: loading ? (
                <Skeleton className="h-4 w-24" />
              ) : (
                cluster?.clusterInfra?.currentState.state
              ),
            },
            {
              Property: "Reason",
              Value: loading ? (
                <Skeleton className="h-4 w-32" />
              ) : (
                cluster?.clusterInfra?.currentState.reason
              ),
            },
            {
              Property: "Zone",
              Value: loading ? (
                <Skeleton className="h-4 w-28" />
              ) : (
                <>
                  {clusterZone}
                  {cluster && (
                    <span>
                      {" "}
                      ({backendToCloudProvider[cluster?.backendType]})
                    </span>
                  )}
                </>
              ),
            },
            ...(user.isStaff && firewallDesc
              ? [
                  {
                    Property: "Firewall",
                    Value: loading ? (
                      <Skeleton className="h-4 w-20" />
                    ) : (
                      firewallDesc
                    ),
                  },
                ]
              : []),
          ]}
          showHeaders={false}
          zebraStriping={false}
        />
      </div>
    </Stack>
  );
};
